import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Trash2, Save, X, Eye, EyeOff, CheckCircle2, Circle, Pencil, MessageCircle, ClipboardCopy, Check } from "lucide-react";
import { buildBudgetMessage, buildWaLink, copyToClipboard } from "@/lib/waMessages";
import { CustomServiceDialog } from "@/components/CustomServiceDialog";
import { PartsCostEditor, partsSummary, COSTS_UPDATED_EVENT } from "@/components/PartsCostEditor";

const BUDGET_STATUS_LABEL = {
  PENDING_QUOTE: "Pendiente de presupuesto",
  QUOTE_SENT: "Presupuesto enviado",
  APPROVED: "Aprobado",
  REJECTED: "Rechazado",
  AUTHORIZED: "Reparación autorizada",
  COMPLETED: "Reparación completada",
};
const BUDGET_STATUS_LIST = Object.keys(BUDGET_STATUS_LABEL);

function fmtPrice(s) {
  if (s.price_type === "FIXED") return `$${s.price}`;
  if (s.price_type === "RANGE") return `$${s.price_min}–$${s.price_max}`;
  if (s.price_type === "FROM") return `desde $${s.price_from}`;
  if (s.price_type === "VARIABLE") return "Variable";
  if (s.price_type === "CONSULT") return "Consultar";
  return "—";
}

export default function BudgetTab({ order, scopeDev, deviceId, isSecondaryView, user, onHistoryChange }) {
  const [budget, setBudget] = useState({ items: [], total: 0, budget_status: "PENDING_QUOTE" });
  const [picker, setPicker] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [customOpen, setCustomOpen] = useState(false);
  // Aprobación manual
  const [manualOpen, setManualOpen] = useState(false);
  const [manualChannel, setManualChannel] = useState(null);
  const [manualConfirming, setManualConfirming] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const orderId = order.id;
  // Device activo (para categoria/subcategoria en la búsqueda de servicios y device_id en creación)
  const dev = scopeDev || order;
  const targetDeviceId = deviceId || order.id;

  const canEdit = ["admin", "supervisor", "reception", "technician"].includes(user.role);
  const canOverridePrice = ["admin", "supervisor"].includes(user.role);
  const canChangeStatus = ["admin", "supervisor", "reception"].includes(user.role) || (user.role === "technician");
  // Todos los roles internos pueden aprobar manualmente
  const canApproveManual = ["admin", "supervisor", "reception", "technician"].includes(user.role);
  const approved = budget.budget_status === "APPROVED";
  const rejected = budget.budget_status === "REJECTED";
  const hasVisibleItems = (budget.items || []).some(i => i.visible_to_client);

  async function approveManual() {
    if (!manualChannel) return;
    setManualSaving(true);
    try {
      const r = await api.post(`/orders/${orderId}/budget/approve-manual`, { channel: manualChannel });
      setBudget(r.data);
      setManualOpen(false);
      setManualConfirming(false);
      setManualChannel(null);
      toast.success("Presupuesto aprobado manualmente");
      onHistoryChange && onHistoryChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setManualSaving(false);
    }
  }

  async function refresh() {
    try {
      const r = await api.get(`/orders/${orderId}/budget?device_id=${targetDeviceId}`);
      setBudget(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [orderId, targetDeviceId]);
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener(COSTS_UPDATED_EVENT, h);
    return () => window.removeEventListener(COSTS_UPDATED_EVENT, h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, targetDeviceId]);

  useEffect(() => {
    if (!picker) return;
    const params = new URLSearchParams({ active: "true" });
    // Filtrar catálogo por la categoría del EQUIPO activo (primary o secundario)
    if (dev.category_id) params.set("category_id", dev.category_id);
    if (dev.subcategory_id) params.set("subcategory_id", dev.subcategory_id);
    if (q) params.set("q", q);
    api.get(`/services?${params.toString()}`).then((r) => setResults(r.data)).catch(() => {});
  }, [picker, q, dev.category_id, dev.subcategory_id]);

  async function addService(svc, unit_price = null) {
    if (approved) return toast.error("Presupuesto aprobado — no se puede modificar");
    // Helper: valida y normaliza precio ingresado por prompt (acepta coma, $, espacios)
    function parsePrompt(raw) {
      if (raw == null || String(raw).trim() === "") return { cancelled: true };
      const n = Number(String(raw).replace(/\$/g, "").replace(/\s/g, "").replace(",", "."));
      if (!Number.isFinite(n) || n < 0) return { invalid: true };
      return { value: n };
    }
    setSaving(true);
    try {
      const body = { service_id: svc.id, quantity: 1, device_id: targetDeviceId };
      if (unit_price != null) body.unit_price = unit_price;
      if (svc.price_type === "VARIABLE") {
        const parsed = parsePrompt(prompt(`Precio para "${svc.name}" (USD)`));
        if (parsed.cancelled) { setSaving(false); return; }
        if (parsed.invalid) { setSaving(false); return toast.error("Precio inválido. Ingresá un número ≥ 0."); }
        body.unit_price = parsed.value;
      }
      if (svc.price_type === "RANGE") {
        const parsed = parsePrompt(prompt(`Precio para "${svc.name}" (rango $${svc.price_min}–$${svc.price_max})`, String(svc.price_min ?? "")));
        if (parsed.cancelled) { setSaving(false); return; }
        if (parsed.invalid) { setSaving(false); return toast.error("Precio inválido. Ingresá un número ≥ 0."); }
        body.unit_price = parsed.value;
      }
      await api.post(`/orders/${orderId}/services`, body);
      toast.success("Servicio agregado");
      setPicker(false); setQ("");
      await refresh();
      onHistoryChange && onHistoryChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function addCustom(data) {
    try {
      await api.post(`/orders/${orderId}/services`, {
        ...data, quantity: 1, price_type: "VARIABLE", device_id: targetDeviceId,
      });
      toast.success(`Servicio personalizado agregado · $${data.unit_price.toFixed(2)}`);
      setCustomOpen(false);
      await refresh();
      onHistoryChange && onHistoryChange();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function startEdit(it) {
    setEditing(it.id);
    setEditVals({ quantity: it.quantity, unit_price: it.unit_price, note: it.note || "", visible_to_client: it.visible_to_client });
  }
  async function saveEdit() {
    try {
      await api.patch(`/orders/${orderId}/services/${editing}`, editVals);
      setEditing(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function removeItem(it) {
    if (!confirm(`¿Quitar "${it.name}" del presupuesto?`)) return;
    try {
      await api.delete(`/orders/${orderId}/services/${it.id}`);
      await refresh();
      onHistoryChange && onHistoryChange();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function toggleVisible(it) {
    try {
      await api.patch(`/orders/${orderId}/services/${it.id}`, { visible_to_client: !it.visible_to_client });
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function changeStatus(next) {
    if (user.role === "technician" && next !== "COMPLETED") {
      return toast.error("Los técnicos solo pueden marcar Reparación completada");
    }
    try {
      const r = await api.patch(`/orders/${orderId}/budget-status`, { budget_status: next });
      setBudget(r.data);
      toast.success(`Estado: ${BUDGET_STATUS_LABEL[next]}`);
      onHistoryChange && onHistoryChange();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const [copied, setCopied] = useState(false);
  const hasItemsInBudget = (budget.items || []).length > 0;
  const phone = order.customer?.phone || "";
  const waLink = phone && hasItemsInBudget ? buildWaLink(phone, buildBudgetMessage(order, budget)) : "";

  async function copyBudget() {
    const t = buildBudgetMessage(order, budget);
    if (!t) return toast.error("Todavía no hay servicios en el presupuesto.");
    const ok = await copyToClipboard(t);
    if (ok) {
      setCopied(true);
      toast.success("Presupuesto copiado al portapapeles");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("No se pudo copiar. Intentá desde HTTPS o pegá manualmente.");
    }
  }

  return (
    <div className="space-y-5">
      {/* Status + Total */}
      <div className="card-base p-4 space-y-3">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Estado del presupuesto</div>
            <div className="text-lg font-heading font-bold text-blue-700" data-testid="budget-status-label">{BUDGET_STATUS_LABEL[budget.budget_status]}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Total</div>
            <div className="text-2xl font-heading font-extrabold text-emerald-600 font-mono" data-testid="budget-total">${budget.total.toFixed(2)}</div>
            {budget.total_estimated_minutes > 0 && (
              <div className="text-[10px] text-slate-500 font-mono">Tiempo estimado: {budget.total_estimated_minutes} min</div>
            )}
            {user.role === "admin" && (budget.total_cost != null || budget.total_profit != null) && (
              <div className="mt-1 text-[11px] font-mono" data-testid="budget-admin-totals">
                <span className="text-amber-700">Costo ${Number(budget.total_cost || 0).toFixed(2)}</span>
                {" · "}
                <span className="text-blue-700 font-bold">Ganancia ${Number(budget.total_profit || 0).toFixed(2)}</span>
              </div>
            )}
            {user.role === "admin" && budget.has_missing_cost && (
              <div className="mt-1 text-[11px] text-amber-700" data-testid="budget-missing-cost-warn">
                Faltan costos — ganancia no calculada
              </div>
            )}
          </div>
        </div>
        {canChangeStatus && (
          <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
            {BUDGET_STATUS_LIST.map((st) => {
              const disabled = st === budget.budget_status || (user.role === "technician" && st !== "COMPLETED");
              return (
                <button key={st} type="button" onClick={() => changeStatus(st)} disabled={disabled}
                  data-testid={`budget-status-${st}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${st === budget.budget_status
                    ? "bg-blue-600 text-white border-blue-600"
                    : disabled ? "bg-slate-50 text-slate-300 border-slate-100" : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"}`}>
                  {BUDGET_STATUS_LABEL[st]}
                </button>
              );
            })}
          </div>
        )}

        {/* Copiar / WhatsApp — mismo presupuesto, mismos botones que en la etapa Reparación */}
        {hasItemsInBudget && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-slate-100" data-testid="budget-share-actions">
            <button
              type="button"
              onClick={copyBudget}
              className="btn-secondary py-3 text-sm min-h-[48px]"
              data-testid="budget-copy-btn"
            >
              {copied ? <><Check size={16} /> Copiado</> : <><ClipboardCopy size={16} /> Copiar presupuesto</>}
            </button>
            {waLink ? (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary py-3 text-sm min-h-[48px] bg-emerald-600 hover:bg-emerald-500"
                data-testid="budget-whatsapp-btn"
              >
                <MessageCircle size={16} /> WhatsApp al cliente
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="btn-secondary py-3 text-sm min-h-[48px] opacity-60 cursor-not-allowed"
                title="Cliente sin teléfono cargado"
                data-testid="budget-whatsapp-btn-disabled"
              >
                <MessageCircle size={16} /> WhatsApp (sin teléfono)
              </button>
            )}
          </div>
        )}
      </div>

      {/* Aprobación manual del taller */}
      {canApproveManual && !approved && !rejected && hasVisibleItems && (
        <div className="card-base p-4 border-blue-200 bg-blue-50" data-testid="budget-manual-approval">
          {!manualOpen ? (
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading font-bold text-slate-900 flex items-center gap-2">
                  ✓ Aprobar manualmente
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Registra que el cliente autorizó la reparación directamente con el personal.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setManualOpen(true)}
                data-testid="budget-manual-approve-open"
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-sm"
              >
                Registrar aprobación
              </button>
            </div>
          ) : !manualConfirming ? (
            <div data-testid="budget-manual-channel-picker">
              <div className="font-heading font-bold text-slate-900 mb-1">¿Cómo aprobó el cliente?</div>
              <div className="text-xs text-slate-600 mb-3">Elegí el canal por el que recibiste la autorización.</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { key: "PHONE", label: "📞 Teléfono" },
                  { key: "WHATSAPP", label: "💬 WhatsApp" },
                  { key: "IN_PERSON", label: "🏪 Personalmente" },
                  { key: "AT_INTAKE", label: "⚡ Al recibir" },
                ].map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setManualChannel(c.key)}
                    data-testid={`budget-manual-channel-${c.key}`}
                    className={`py-3 rounded-xl text-sm font-semibold border-2 transition-colors ${
                      manualChannel === c.key
                        ? "bg-blue-600 text-white border-blue-700"
                        : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => { setManualOpen(false); setManualChannel(null); }}
                  className="px-4 py-2 rounded-xl bg-white text-slate-700 border border-slate-200 text-sm font-semibold"
                  data-testid="budget-manual-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => setManualConfirming(true)}
                  disabled={!manualChannel}
                  className="flex-1 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold shadow-sm"
                  data-testid="budget-manual-next"
                >
                  Continuar
                </button>
              </div>
            </div>
          ) : (
            <div className="border-2 border-emerald-300 bg-white rounded-xl p-3" data-testid="budget-manual-confirm">
              <div className="font-heading font-bold text-slate-900 mb-1">¿Confirmás que el cliente autorizó este presupuesto?</div>
              <div className="text-xs text-slate-600 mb-3">
                Canal: <b>{{PHONE: "Teléfono", WHATSAPP: "WhatsApp", IN_PERSON: "Personalmente", AT_INTAKE: "Al recibir el equipo"}[manualChannel]}</b> · Total: <b>${budget.total.toFixed(2)}</b>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setManualConfirming(false)}
                  className="px-4 py-2 rounded-xl bg-white text-slate-700 border border-slate-200 text-sm font-semibold"
                  data-testid="budget-manual-back"
                >
                  Volver
                </button>
                <button
                  onClick={approveManual}
                  disabled={manualSaving}
                  className="flex-1 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold shadow-sm"
                  data-testid="budget-manual-confirm-btn"
                >
                  {manualSaving ? "Registrando…" : "✓ Confirmar aprobación"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Chip cuando ya fue aprobado manualmente */}
      {approved && order.budget_decision_by === "INTERNAL" && order.budget_approval_channel && (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="budget-manual-approved-chip">
          <div className="font-heading font-bold text-emerald-900 flex items-center gap-2">
            ✓ Aprobado manualmente
          </div>
          <div className="text-xs text-emerald-800 mt-1">
            Canal: <b>{{PHONE: "Teléfono", WHATSAPP: "WhatsApp", IN_PERSON: "Personalmente en tienda", AT_INTAKE: "Al recibir el equipo"}[order.budget_approval_channel]}</b>
            {order.budget_decision_by_user_name && <> · Por <b>{order.budget_decision_by_user_name}</b></>}
            {order.budget_decision_at && <> · {new Date(order.budget_decision_at).toLocaleString("es-AR")}</>}
          </div>
        </div>
      )}

      {/* Chip cuando fue aprobado por el cliente (QR) */}
      {approved && order.budget_decision_by === "CLIENT" && (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="budget-client-approved-chip">
          <div className="font-heading font-bold text-emerald-900">✓ Aprobado por cliente desde el Portal Público</div>
          {order.budget_decision_at && (
            <div className="text-xs text-emerald-800 mt-0.5">{new Date(order.budget_decision_at).toLocaleString("es-AR")}</div>
          )}
        </div>
      )}

      {/* Items */}
      <div className="card-base p-4 space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-600">Servicios ({budget.items.length})</h3>
          {canEdit && !approved && (
            <div className="flex gap-1">
              <button onClick={() => setPicker(true)} className="btn-primary text-xs py-1.5" data-testid="budget-add-service"><Plus size={14} /> Agregar</button>
              <button onClick={() => setCustomOpen(true)} className="btn-secondary text-xs py-1.5" data-testid="budget-add-custom"><Pencil size={12} /> Custom</button>
            </div>
          )}
        </div>
        {approved && <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-700">Presupuesto aprobado — precios congelados.</div>}
        {budget.items.length === 0 && <div className="text-slate-500 text-sm text-center py-6">Sin servicios agregados aún.</div>}
        <ul className="space-y-2">
          {budget.items.map((it) => {
            const isEditing = editing === it.id;
            return (
              <li key={it.id} className="rounded-xl border border-slate-200 p-3" data-testid={`budget-item-${it.id}`}>
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="font-semibold text-slate-800 text-sm">{it.name}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" min="1" value={editVals.quantity} onChange={(e) => setEditVals({ ...editVals, quantity: Number(e.target.value) })} placeholder="Cantidad" className="input-base py-1.5 text-sm font-mono" />
                      <input type="number" step="0.01" value={editVals.unit_price} onChange={(e) => setEditVals({ ...editVals, unit_price: Number(e.target.value) })} placeholder="Precio unit." className="input-base py-1.5 text-sm font-mono" disabled={it.price_type === "FIXED" && !canOverridePrice} />
                    </div>
                    <input value={editVals.note} onChange={(e) => setEditVals({ ...editVals, note: e.target.value })} placeholder="Nota del servicio" className="input-base py-1.5 text-sm" />
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditing(null)} className="btn-secondary text-xs py-1"><X size={12} /> Cancelar</button>
                      <button onClick={saveEdit} className="btn-primary text-xs py-1"><Save size={12} /> Guardar</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800 text-sm truncate">{it.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {it.quantity} × ${it.unit_price.toFixed(2)} {it.price_type !== "FIXED" && <span className="text-amber-600">({it.price_type})</span>}
                      </div>
                      {it.note && <div className="text-[11px] text-slate-500 italic mt-0.5">{it.note}</div>}
                      {user.role === "admin" ? (
                        <PartsCostEditor orderId={order.id} item={it} />
                      ) : !it.service_id && it.parts_policy && (
                        <div className={`text-[11px] font-mono mt-0.5 ${partsSummary(it).tone}`} data-testid={`budget-parts-${it.id}`}>{partsSummary(it).text}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-bold text-slate-900" data-testid={`budget-subtotal-${it.id}`}>${it.subtotal.toFixed(2)}</div>
                      {user.role === "admin" && it.profit != null && (
                        <div className="text-[11px] text-emerald-700 font-mono" data-testid={`budget-profit-${it.id}`}>
                          Gan. ${it.profit.toFixed(2)}
                        </div>
                      )}
                      <div className="flex gap-1 mt-1 justify-end">
                        {canEdit && !approved && (
                          <>
                            <button onClick={() => toggleVisible(it)} className={`p-1 rounded ${it.visible_to_client ? "text-emerald-600" : "text-slate-400"}`} title={it.visible_to_client ? "Visible al cliente" : "Oculto"}>
                              {it.visible_to_client ? <Eye size={12} /> : <EyeOff size={12} />}
                            </button>
                            <button onClick={() => startEdit(it)} className="p-1 rounded text-blue-600 hover:bg-blue-50"><Pencil size={12} /></button>
                            <button onClick={() => removeItem(it)} className="p-1 rounded text-red-500 hover:bg-red-50" data-testid={`budget-remove-${it.id}`}><Trash2 size={12} /></button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <CustomServiceDialog open={customOpen} onClose={() => setCustomOpen(false)} onSubmit={addCustom} />

      {/* Picker Modal */}
      {picker && (
        <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setPicker(false)}>
          <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div>
                <h3 className="font-heading font-bold">Agregar servicio</h3>
                <div className="text-[10px] text-slate-500 font-mono">Filtrado por categoría de la orden</div>
              </div>
              <button onClick={() => setPicker(false)} className="p-1 rounded hover:bg-slate-200"><X size={18} /></button>
            </div>
            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="input-base pl-9" data-testid="budget-picker-search" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {results.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">Sin resultados</div>}
              {results.map((s) => (
                <button key={s.id} onClick={() => addService(s)} disabled={saving}
                  className="w-full text-left p-3 border-b border-slate-100 hover:bg-blue-50 disabled:opacity-50"
                  data-testid={`budget-picker-opt-${s.id}`}>
                  <div className="flex justify-between items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-800 text-sm">{s.name}</div>
                      {s.notes && <div className="text-[10px] text-slate-500 italic">{s.notes}</div>}
                    </div>
                    <div className="font-mono text-sm font-semibold text-emerald-700 shrink-0">{fmtPrice(s)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
